// useDatabaseSchemaState.js

import { useMemo } from 'react';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { getResolvedFields } from 'utilities/NodeType';
import buildCatchAllFacetFields from 'views/utils/buildCatchAllFacetFields';
import { buildCatchAllBoardFields, buildCatchAllPropertyFields } from 'views/utils/organicViews';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';

/**
 * useDatabaseSchemaState
 *
 * Derives schema-backed field lists and type option metadata for the active database type.
 *
 * @param {object} params - Hook parameters.
 * @param {string} params.typeKey - Active node type key.
 * @param {number} params.nodeTypeCount - Current node type count revision dependency.
 * @param {number} params.nodeTypesRevision - Current node type revision dependency.
 * @param {function} params.getAll - Returns all node type definitions.
 * @param {string} params.language - Active language key.
 * @param {boolean} [params.organicViewsEnabled=false] - Expand lens fields for organic views.
 * @param {Array} [params.nodes] - Current database nodes for Tags lens options.
 *
 * @returns {object} Derived schema state for Database.
 */
const useDatabaseSchemaState = ({
    typeKey,
    nodeTypeCount,
    nodeTypesRevision,
    getAll,
    language,
    organicViewsEnabled = false,
    nodes = null,
}) => {
    const resolvedProperties = useMemo(() => {
        void nodeTypeCount;
        void nodeTypesRevision;
        return getResolvedFields(typeKey, { includeHidden: true, excludeSecured: true });
    }, [typeKey, nodeTypeCount, nodeTypesRevision]);

    const nodeTypeOptions = useMemo(() => {
        const rawTypes = Array.isArray(getAll?.()) ? getAll() : [];
        return rawTypes
            .map((typeDoc) => {
                const key = String(typeDoc?.key || '').trim();
                if (!key) return null;

                const label = getSingularLabel(typeDoc, key, language) || '';
                const fields = Array.isArray(getResolvedFields(key, { includeHidden: true, excludeSecured: true }))
                    ? getResolvedFields(key, { includeHidden: true, excludeSecured: true })
                    : [];
                const peopleFields = (Array.isArray(getResolvedFields(key, { includeHidden: true, excludeSecured: true }))
                    ? getResolvedFields(key, { includeHidden: true, excludeSecured: true })
                    : [])
                    .filter((field) => String(field?.type || '').trim().toLowerCase() === 'people')
                    .map((field) => {
                        const fieldKey = String(field?.name || field?.key || '').trim();
                        if (!fieldKey) return null;
                        return {
                            key: fieldKey,
                            label: String(field?.label || '').trim(),
                        };
                    })
                    .filter(Boolean);

                return {
                    fields,
                    key,
                    label,
                    peopleFields,
                };
            })
            .filter(Boolean)
            .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
    }, [getAll, language]);

    const schemaFieldMap = useMemo(() => {
        const map = new Map();
        (Array.isArray(resolvedProperties) ? resolvedProperties : []).forEach((field) => {
            const key = field?.name || field?.key;
            if (key) map.set(key, field);
        });
        return map;
    }, [resolvedProperties]);

    const searchableSchemaFields = useMemo(() => {
        return (Array.isArray(resolvedProperties) ? resolvedProperties : []).filter((field) => {
            const type = String(field?.type || '').trim().toLowerCase();
            return type === 'string' || type === 'enum' || type === 'richtext';
        });
    }, [resolvedProperties]);

    const catchAllFacetFields = useMemo(() => (
        buildCatchAllFacetFields(resolvedProperties, language, {
            includeMultiselect: organicViewsEnabled,
            includeTags: organicViewsEnabled,
            items: nodes,
        })
    ), [language, nodes, organicViewsEnabled, resolvedProperties]);

    const catchAllBoardFields = useMemo(() => (
        organicViewsEnabled ? buildCatchAllBoardFields(resolvedProperties) : []
    ), [organicViewsEnabled, resolvedProperties]);

    const catchAllPropertyFields = useMemo(() => (
        organicViewsEnabled ? buildCatchAllPropertyFields(resolvedProperties) : []
    ), [organicViewsEnabled, resolvedProperties]);

    const catchAllCalendarFields = useMemo(() => (
        catchAllPropertyFields.filter((field) => isTemporalFieldType(field.type))
    ), [catchAllPropertyFields]);

    return {
        resolvedProperties,
        nodeTypeOptions,
        schemaFieldMap,
        searchableSchemaFields,
        catchAllFacetFields,
        catchAllBoardFields,
        catchAllPropertyFields,
        catchAllCalendarFields,
    };
};

export default useDatabaseSchemaState;
